<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  dateFormat,
  dynamicTime,
  shortTimestamp,
} from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
  avatarSrc: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['uploadAvatar', 'deleteAvatar']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const LIFECYCLE_STAGES = ['visitor', 'lead', 'customer'];

const STAGE_BADGE_STYLES = {
  visitor: 'bg-n-slate-3 text-n-slate-11',
  lead: 'bg-n-amber-3 text-n-amber-11',
  customer: 'bg-n-teal-3 text-n-teal-11',
};

const STAGE_ACTIVE_TEXT_STYLES = {
  visitor: 'text-n-slate-12',
  lead: 'text-n-amber-11',
  customer: 'text-n-teal-11',
};

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const conversationsByContactId = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);

const currentStage = computed(
  () => props.selectedContact?.contactType || 'visitor'
);

const stageLabel = stage =>
  t(`CONTACTS_LAYOUT.DETAILS.LIFECYCLE.${stage.toUpperCase()}`);

const contactConversations = computed(() =>
  conversationsByContactId.value(route.params.contactId)
);

const openConversationsCount = computed(
  () =>
    contactConversations.value.filter(({ status }) => status === 'open').length
);

const createdAt = computed(() =>
  props.selectedContact?.createdAt
    ? dynamicTime(props.selectedContact.createdAt)
    : ''
);

const lastActivityAt = computed(() =>
  props.selectedContact?.lastActivityAt
    ? dynamicTime(props.selectedContact.lastActivityAt)
    : ''
);

const factsLine = computed(() => {
  const {
    email,
    identifier,
    additionalAttributes = {},
  } = props.selectedContact || {};
  const { city, country, companyName } = additionalAttributes || {};
  const location = [city, country].filter(Boolean).join(', ');
  return [email, identifier, location, companyName].filter(Boolean).join(' · ');
});

// Mirrors Contacts::SyncAttributes#set_contact_type — the server promotes any
// visitor with contact details back to lead, so offering "Visitor" would lie.
const hasContactDetails = computed(() => {
  const {
    email,
    phoneNumber,
    additionalAttributes = {},
  } = props.selectedContact || {};
  const hasSocialDetails = Object.entries(additionalAttributes || {}).some(
    ([key, value]) =>
      key.startsWith('social') &&
      (typeof value === 'object' && value !== null
        ? Object.values(value).some(Boolean)
        : Boolean(value))
  );
  return Boolean(email || phoneNumber || hasSocialDetails);
});

const isStageDisabled = stage =>
  stage === 'visitor' &&
  stage !== currentStage.value &&
  hasContactDetails.value;

const statTiles = computed(() => [
  {
    key: 'conversations',
    label: t('CONTACTS_LAYOUT.DETAILS.STATS.CONVERSATIONS'),
    value: contactConversations.value.length,
    sub: openConversationsCount.value
      ? t('CONTACTS_LAYOUT.DETAILS.STATS.OPEN_COUNT', {
          count: openConversationsCount.value,
        })
      : '',
  },
  {
    key: 'firstSeen',
    label: t('CONTACTS_LAYOUT.DETAILS.STATS.FIRST_SEEN'),
    value: props.selectedContact?.createdAt
      ? dateFormat(props.selectedContact.createdAt, 'MMM yyyy')
      : '—',
    sub: '',
  },
  {
    key: 'lastActive',
    label: t('CONTACTS_LAYOUT.DETAILS.STATS.LAST_ACTIVE'),
    value: props.selectedContact?.lastActivityAt
      ? shortTimestamp(dynamicTime(props.selectedContact.lastActivityAt), true)
      : '—',
    sub: '',
  },
]);

const setLifecycleStage = async stage => {
  if (stage === currentStage.value || isUpdating.value) return;

  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      contactType: stage,
    });
    useAlert(
      t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.UPDATE_SUCCESS_MESSAGE', {
        stage: stageLabel(stage),
      })
    );
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.UPDATE_ERROR_MESSAGE'));
  }
};
</script>

<template>
  <CardLayout>
    <div class="flex flex-wrap items-center gap-4">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="64"
        allow-upload
        @upload="emit('uploadAvatar', $event)"
        @delete="emit('deleteAvatar')"
      />
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3
            class="text-lg font-semibold tracking-tight truncate text-n-slate-12"
          >
            {{ selectedContact?.name }}
          </h3>
          <span
            class="inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-xs font-medium"
            :class="STAGE_BADGE_STYLES[currentStage]"
          >
            <span class="rounded-full size-1.5 bg-current" />
            {{ stageLabel(currentStage) }}
          </span>
        </div>
        <span v-if="factsLine" class="text-sm truncate text-n-slate-11">
          {{ factsLine }}
        </span>
        <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
          <span class="i-ph-activity text-n-slate-10 size-4 shrink-0" />
          {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
          •
          {{
            $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
              date: lastActivityAt,
            })
          }}
        </span>
      </div>
      <div
        class="items-stretch hidden overflow-hidden border md:flex rounded-xl border-n-weak divide-x divide-n-weak rtl:divide-x-reverse"
      >
        <div
          v-for="tile in statTiles"
          :key="tile.key"
          class="flex flex-col justify-center px-4 py-2 min-w-[5.5rem]"
        >
          <span
            class="text-[11px] font-medium tracking-wide uppercase text-n-slate-10"
          >
            {{ tile.label }}
          </span>
          <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
            {{ tile.value }}
          </span>
          <span v-if="tile.sub" class="text-xs text-n-slate-11">
            {{ tile.sub }}
          </span>
        </div>
      </div>
    </div>
    <ContactLabels :contact-id="selectedContact?.id" />
    <div
      class="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.TITLE') }}
      </span>
      <div class="flex items-center p-0.5 rounded-lg bg-n-alpha-2">
        <button
          v-for="stage in LIFECYCLE_STAGES"
          :key="stage"
          type="button"
          class="px-3 text-sm font-medium transition-colors rounded-md h-7 disabled:cursor-not-allowed disabled:opacity-60"
          :class="
            stage === currentStage
              ? `bg-n-solid-1 shadow-sm ${STAGE_ACTIVE_TEXT_STYLES[stage]}`
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :disabled="isUpdating || isStageDisabled(stage)"
          :title="
            isStageDisabled(stage)
              ? t('CONTACTS_LAYOUT.DETAILS.LIFECYCLE.VISITOR_DISABLED_TOOLTIP')
              : undefined
          "
          @click="setLifecycleStage(stage)"
        >
          {{ stageLabel(stage) }}
        </button>
      </div>
    </div>
  </CardLayout>
</template>
